@file:OptIn(ExperimentalBlockRichTextApi::class)

package com.taocent.simple.compose.component.blockrichtext

import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue

/**
 * BlockState 块/表格插入相关的扩展函数。
 *
 * - [insertBlockAfter] 在指定块后插入空 TextBlock
 * - [insertTable] 在光标位置按规则插入 TableBlock
 * - [insertOrExpandTextBeforeTable] 在选中表格上方插入/扩展文本块
 */

fun BlockState.insertBlockAfter(afterBlockId: String): TextBlock {
    val before = documentSnapshot()
    val newBlock = TextBlock()
    val index = findBlockIndex(afterBlockId)
    if (index >= 0) {
        _blocks.add(index + 1, newBlock)
    } else {
        _blocks.add(newBlock)
    }
    blockStates[newBlock.id] = createBlockState()
    setFocusedBlockCompat(newBlock.id)
    recordDocumentChange(before)
    return newBlock
}

fun BlockState.insertTable(rows: Int, columns: Int) {
    val before = documentSnapshot()
    val wasNavSelected = navSelectedTableId != null
    val navTableId = navSelectedTableId
    val restoreFocusBlockId = focusedBlockId
    val tableBlock = TableBlock.create(rows, columns)

    if (wasNavSelected && navTableId != null) {
        val navIndex = findBlockIndex(navTableId)
        if (navIndex >= 0) {
            _blocks.add(navIndex, tableBlock)
            setBlockAnchorCursor(navTableId)
            restoreTextFocusIfNeeded(restoreFocusBlockId)
            recordDocumentChange(before)
            return
        }
    }

    val currentId = focusedBlockId
    val currentBlock = _blocks.firstOrNull { it.id == currentId }
    val index = findBlockIndex(currentId)

    // 仅当"新插入的 TableBlock 后面没有可承接光标的 TextBlock"时,才需要追加一个空 TextBlock
    // 作为 placeholder。判定条件:
    //   - nextIdx 越界(表格是文档末块)→ 必须 placeholder,否则用户无法在表格后继续输入
    //   - _blocks[nextIdx] 是 TableBlock(连续两表)→ 必须 placeholder,中间隔开
    //   - _blocks[nextIdx] 是 TextBlock → 不需要 placeholder,光标可直接落在该 TextBlock
    //
    // 旧实现写成 `_blocks[nextIdx] !is TableBlock`(即下一格是 TextBlock 反而插 placeholder),
    // 在"锚点 TA 选中态(focusedBlockId 仍指向 TA 之后的 T2)→ 工具栏插表格"等路径上会
    // 多出一个空文本块。修正为 `is TableBlock`。
    fun shouldInsertTextAfter(insertIndex: Int): Boolean {
        val nextIdx = insertIndex + 1
        return nextIdx >= _blocks.size || _blocks[nextIdx] is TableBlock
    }

    if (currentBlock is TextBlock) {
        val currentState = getBlockState(currentBlock.id)
        val cursorPos = currentState.textFieldValue.selection.min
        val content = currentState.textFieldValue.annotatedString
        val contentLength = content.length

        when {
            contentLength == 0 -> {
                // 空文本块插入表格:不创建额外空文本块(项目硬约束:"不应额外创建空文本块")。
                // 保留原 TextBlock 在 TableBlock 后,作为 TableBlock 后的光标落点 —
                // 等价于"cursorPos == 0"的拆分:TableBlock 插入在原 TextBlock 前,
                // 原 TextBlock 移到 index+1。这样 LazyColumn 看到的 key diff 是:
                //   t1(原 id)位置 0 → 1 → 触发 placement 动画
                //   tbl1 新 key → 触发 fadeIn(alpha 0→1,220ms 可见)
                // 而不是旧实现的"原地替换 + 凭空多出一个 t2",后者两个 0 高度 item
                // 互相覆盖,TableBlock 的 fadeIn 视觉上几乎只有"瞬间切换"感。
                // 焦点保留在原 TextBlock,requestFocus = true 自增 focusRequestVersion,
                // 让顶层 LaunchedEffect 重新把焦点放回 t1(原 RichTextState 保留,
                // 光标位置 / IME 状态 / undo 历史都不丢)。
                _blocks.add(index, tableBlock)
                setFocusedBlockCompat(currentBlock.id, requestFocus = true)
            }
            cursorPos == 0 -> {
                _blocks.add(index, tableBlock)
                if (shouldInsertTextAfter(index)) {
                    val textBlockAfter = TextBlock()
                    _blocks.add(index + 1, textBlockAfter)
                    blockStates[textBlockAfter.id] = createBlockState()
                    setFocusedBlockCompat(textBlockAfter.id)
                } else {
                    focusNearestTextBlock(index)
                }
            }
            cursorPos >= contentLength -> {
                // 光标在 T1 末尾 + 已有 [T1, ..., ...] 时,T1 自己就是新表格"前面"的
                // 承接 TextBlock。光标应留在 T1 末尾,**不**额外创建 placeholder —
                // 项目硬约束:"不应额外创建空文本块"(参见 contentLength == 0 分支注释)。
                //
                // 旧实现 `shouldInsertTextAfter(index+1)` 在下方相邻是 TableBlock 时
                // 强制塞 placeholder + 把光标搬过去,得到 `[T1, TA_new, T_new, TA, T2]`,
                // 既多了一个空 block 又错位了光标。
                //
                // 正确语义:
                //   - 表格不是文档末块:光标留 T1 末尾(用户原位置),无需 placeholder。
                //     如果下方相邻是 TableBlock(连续两表),不需要中间夹 TextBlock 就能编辑,
                //     因为用户的下一个意图(若有)可以通过方向键 ↓ 直接进下一表 cell。
                //   - 表格是文档末块:必须 placeholder,否则文档末尾无地方落光标。
                _blocks.add(index + 1, tableBlock)
                val isLastBlock = index + 1 >= _blocks.size - 1
                if (isLastBlock) {
                    val textBlockAfter = TextBlock()
                    _blocks.add(index + 2, textBlockAfter)
                    blockStates[textBlockAfter.id] = createBlockState()
                    setFocusedBlockCompat(currentBlock.id, requestFocus = true)
                } else {
                    setFocusedBlockCompat(currentBlock.id, requestFocus = true)
                }
            }
            else -> {
                val leftContent = content.subSequence(0, cursorPos)
                val rightContent = content.subSequence(cursorPos, contentLength)

                currentState.restoreTextFieldValue(
                    TextFieldValue(
                        annotatedString = leftContent,
                        selection = TextRange(leftContent.length)
                    )
                )
                syncBlockContent(currentBlock.id)

                val rightTextBlock = TextBlock(content = rightContent)
                val rightState = createBlockState()
                rightState.restoreTextFieldValue(
                    TextFieldValue(
                        annotatedString = rightContent,
                        selection = TextRange(0)
                    )
                )
                blockStates[rightTextBlock.id] = rightState

                _blocks.add(index + 1, tableBlock)
                _blocks.add(index + 2, rightTextBlock)
                setFocusedBlockCompat(rightTextBlock.id)
            }
        }
    } else {
        if (index >= 0) {
            _blocks.add(index + 1, tableBlock)
            if (shouldInsertTextAfter(index + 1)) {
                val textBlockAfter = TextBlock()
                _blocks.add(index + 2, textBlockAfter)
                blockStates[textBlockAfter.id] = createBlockState()
                setFocusedBlockCompat(textBlockAfter.id)
            } else {
                focusNearestTextBlock(index + 1)
            }
        } else {
            _blocks.add(tableBlock)
            val textBlockAfter = TextBlock()
            _blocks.add(textBlockAfter)
            blockStates[textBlockAfter.id] = createBlockState()
            setFocusedBlockCompat(textBlockAfter.id)
        }
    }
    recordDocumentChange(before)
}

fun BlockState.insertOrExpandTextBeforeTable(tableId: String): Boolean {
    val index = findBlockIndex(tableId)
    if (index < 0) return false
    val before = documentSnapshot()
    val prevBlock = if (index > 0) _blocks[index - 1] else null
    if (prevBlock is TextBlock) {
        val state = getBlockState(prevBlock.id)
        val currentText = state.textFieldValue.text
        state.restoreTextFieldValue(
            androidx.compose.ui.text.input.TextFieldValue(
                text = currentText + "\n",
                selection = androidx.compose.ui.text.TextRange(currentText.length + 1)
            )
        )
        setFocusedBlockCompat(prevBlock.id)
        selectedTableBlockId = null
        recordDocumentChange(before)
        return true
    } else {
        val newBlock = TextBlock()
        _blocks.add(index, newBlock)
        blockStates[newBlock.id] = createBlockState()
        setFocusedBlockCompat(newBlock.id)
        selectedTableBlockId = null
        recordDocumentChange(before)
        return true
    }
}
